import { CdkScrollable } from '@angular/cdk/scrolling';
import { ElementRef, inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { skip } from 'rxjs';

import { ExpandService } from '../../../../util-common/src/lib/expand.service';

@Injectable()
export class ScrollService {
  private route = inject(ActivatedRoute);
  private expandService = inject(ExpandService);

  private scrollable: CdkScrollable;
  private elements = signal(new Map<string, ElementRef<HTMLElement>>());
  private flowcharts = signal(new Map<string, boolean>());
  public flowchartElementToHighlight = signal<string>('');
  private pendingScrollFragment: string | null = null;

  constructor() {
    this.route.paramMap.pipe(skip(1)).subscribe(() => {
      this.elements.set(new Map());
      this.flowcharts.set(new Map());
      this.pendingScrollFragment = null;
    });
  }

  public init(scrollable: CdkScrollable) {
    this.scrollable = scrollable;

    this.route.fragment.subscribe((fragment) => {
      if (!fragment) return;
      this.expandService.expandAll();
      this.pendingScrollFragment = fragment;
      this.scrollToFragment(fragment);
    });
  }

  public registerFlowchart(chapterId: string): void {
    this.flowcharts.update((existingMap) => {
      const newMap = new Map(existingMap);
      newMap.set(chapterId, false);
      return newMap;
    });
  }

  public setFlowchartLoaded(chapterId: string): void {
    this.flowcharts.update((existingMap) => {
      const newMap = new Map(existingMap);
      newMap.set(chapterId, true);
      return newMap;
    });

    if (this.pendingScrollFragment) {
      this.scrollToFragment(this.pendingScrollFragment);
    }
  }

  public register(element: ElementRef<HTMLElement>): void {
    const uniqueId = element.nativeElement.getAttribute('data-uniqueid');
    if (!uniqueId) {
      return;
    }

    this.elements.update((existingSectionsMap) => {
      const newSectionsMap = new Map(existingSectionsMap);
      newSectionsMap.set(uniqueId, element);
      return newSectionsMap;
    });

    if (this.pendingScrollFragment) {
      this.scrollToFragment(this.pendingScrollFragment);
    }
  }

  public scrollTo(id: string, smooth = true, highlightElement = false): void {
    if (!this.scrollable) return;

    const element = this.elements().get(id);
    if (!element) return;

    this.expandService.setExpandState(id, true);

    setTimeout(() => {
      const container = this.scrollable.getElementRef().nativeElement;
      const target = container.querySelector(`[data-uniqueid="${id}"]`);
      if (!target) return;

      const top = target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 100;
      this.scrollable.scrollTo({ top, behavior: smooth ? 'smooth' : 'auto' });

      if (highlightElement) {
        this.highlightElement(element);
      }
    }, 200);
  }

  public scrollToFlowchartElement(elementId: string) {
    const chapterElementId = document
      .querySelector<HTMLElement>(`[model-id="${elementId}"]`)
      .closest('[data-widget-type="chapter"]')
      ?.getAttribute('data-uniqueid');

    if (chapterElementId) {
      this.scrollTo(chapterElementId, true, true);
    }

    setTimeout(() => {
      this.flowchartElementToHighlight.set(elementId);
    }, 400);
  }

  private highlightElement(element: ElementRef<HTMLElement>) {
    element.nativeElement.classList.add('scroll-highlight');
    setTimeout(() => {
      element.nativeElement.classList.remove('scroll-highlight');
    }, 5000);
  }

  private scrollToFragment(fragment: string): void {
    const elementUniqueId = fragment.toLowerCase();
    const element =
      document.getElementById(elementUniqueId) ||
      document.querySelector(`[data-uniqueid="${elementUniqueId}"]`) ||
      document.querySelector(`[unique-id="${elementUniqueId}"]`);

    if (!element) {
      return;
    }

    if (element.tagName.toLowerCase() === 'tt-component-widget') {
      const id = element.getAttribute('data-uniqueid');
      if (!id || !this.elements().get(id)) {
        return;
      }

      this.scrollTo(id, true, true);
      this.pendingScrollFragment = null;
    } else if (element.parentNode?.nodeName.toLowerCase() === 'g') {
      const flowchartId = element?.closest('.joint-cell')?.getAttribute('model-id');
      if (!flowchartId) {
        return;
      }

      this.scrollToFlowchartElement(flowchartId);
      this.pendingScrollFragment = null;
    } else {
      const chapterContainer = element.closest('.smartbook-chapter-container');
      const chapterId = chapterContainer?.getAttribute('data-uniqueid');
      if (!chapterId || !this.elements().get(chapterId)) {
        return;
      }

      this.scrollTo(chapterId, true, true);
      this.pendingScrollFragment = null;
    }
  }
}
